/**
 * Elevation shared primitives — v3 "daylight editorial"
 * ─────────────────────────────────────────────────────────────────
 * Every Elevation page MUST use these components instead of writing
 * its own card/row/badge/section styling.  Visual correctness lives
 * in ONE place; pages stop drifting.
 *
 * v3 (deck-derived, ratified 2026-08-29):
 *   Ground   — warm paper via the .daylight scoped tokens (src/index.css);
 *              under .dark the same --p-* names remap onto the dark ramp,
 *              so primitives carry NO dark: variants.
 *   Cards    — bg-[var(--p-shade)], 1px var(--p-hairline), square corners
 *   Display  — font-serif (Spectral) in ink; gold is spent one phrase at a time
 *   Eyebrows — font-body uppercase tracking-[0.28em] in slate
 *   Data     — font-mono tabular in ink
 *   No shadows, no border-2, no gradients, no rounded corners.
 */

import React from 'react';

function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

// ── Page shell ────────────────────────────────────────────────────

interface PageShellProps {
  children: React.ReactNode;
  className?: string;
}

/** Outermost wrapper for every Elevation page.  Sets max-width, padding, font.
 *  Width and side padding match the bar above (TeamHomeBar, ProductDesignDocsNav),
 *  so the page uses the same edges as its header. */
export function PageShell({ children, className }: PageShellProps) {
  return (
    <div className={cn('max-w-6xl mx-auto px-4 sm:px-6 py-8 font-body text-[var(--p-ink)]', className)}>
      {children}
    </div>
  );
}

// ── Section ───────────────────────────────────────────────────────

interface SectionProps {
  children: React.ReactNode;
  className?: string;
}

export function Section({ children, className }: SectionProps) {
  return <div className={cn('mb-10', className)}>{children}</div>;
}

// ── Section label (eyebrow) ───────────────────────────────────────

interface SectionLabelProps {
  children: React.ReactNode;
  className?: string;
}

/** Wide-tracked uppercase eyebrow above a heading — slate, quiet. */
export function SectionLabel({ children, className }: SectionLabelProps) {
  return (
    <p className={cn('font-body text-[11px] font-medium uppercase tracking-[0.28em] text-[var(--p-label)] mb-1', className)}>
      {children}
    </p>
  );
}

// ── Heading ───────────────────────────────────────────────────────

interface HeadingProps {
  children: React.ReactNode;
  as?: 'h1' | 'h2' | 'h3' | 'h4';
  size?: 'xl' | 'lg' | 'md' | 'sm';
  className?: string;
}

const headingSize = {
  xl: 'text-4xl tracking-[-0.01em]',
  lg: 'text-2xl tracking-[-0.005em]',
  md: 'text-[19px]',
  sm: 'text-[16px]',
};

/** Display heading — Spectral in ink, normal weight. The voice of the page. */
export function Heading({ children, as: Tag = 'h2', size = 'lg', className }: HeadingProps) {
  return (
    <Tag className={cn('font-serif font-normal leading-tight text-[var(--p-ink)]', headingSize[size], className)}>
      {children}
    </Tag>
  );
}

// ── Card ──────────────────────────────────────────────────────────

interface CardProps {
  children: React.ReactNode;
  className?: string;
  /** Set true when the card contains a row list — removes default padding so rows go edge-to-edge */
  flush?: boolean;
  /** Featured plate: carries the 3px gold top bar. Spend it sparingly. */
  featured?: boolean;
}

/** Standard Elevation card.  Paper-shade plate, hairline border, square. */
export function Card({ children, className, flush, featured }: CardProps) {
  return (
    <div
      className={cn(
        'bg-[var(--p-shade)] border border-[var(--p-hairline)] overflow-hidden',
        featured && 'border-t-[3px] border-t-[var(--p-gold)]',
        !flush && 'p-6',
        className
      )}
    >
      {children}
    </div>
  );
}

// ── Card header (title + optional action, top of a card) ─────────

interface CardHeaderProps {
  label?: string;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  count?: number | string;
  className?: string;
}

export function CardHeader({ label, title, subtitle, action, count, className }: CardHeaderProps) {
  return (
    <div className={cn('px-5 pt-5 pb-4 border-b border-[var(--p-hairline)] flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        {label && <SectionLabel>{label}</SectionLabel>}
        <Heading size="md">{title}</Heading>
        {subtitle && (
          <p className="text-sm text-[var(--p-ink-soft)] mt-0.5">{subtitle}</p>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {count !== undefined && (
          <Badge variant="outline">{count}</Badge>
        )}
        {action}
      </div>
    </div>
  );
}

// ── Row list ──────────────────────────────────────────────────────

interface RowListProps {
  children: React.ReactNode;
  className?: string;
}

/** Container for a list of <Row> items. Applies hairline dividers. */
export function RowList({ children, className }: RowListProps) {
  return (
    <div className={cn('divide-y divide-[var(--p-hairline)]', className)}>
      {children}
    </div>
  );
}

// ── Row ───────────────────────────────────────────────────────────

interface RowProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

export function Row({ children, onClick, className }: RowProps) {
  return (
    <div
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 px-5 py-3 text-sm text-[var(--p-ink-soft)]',
        onClick && 'cursor-pointer hover:bg-[var(--hover-tint)] transition-colors',
        className
      )}
    >
      {children}
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────────────

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'outline' | 'green' | 'amber' | 'red' | 'blue';
  className?: string;
}

/* Accent badges tint with the semantic v3 colors: forest = health,
   gold = priority, clay = urgency, slate = structure. Fills stay quiet
   (12% ink of the accent) so the label carries the meaning. */
const badgeVariants: Record<string, string> = {
  default:  'bg-[var(--hover-lift)] text-[var(--p-ink-soft)]',
  outline:  'border border-[var(--p-hairline-2)] text-[var(--p-ink-soft)] bg-transparent',
  green:    'bg-[color-mix(in_srgb,var(--p-forest)_12%,transparent)] text-[var(--p-forest)] dark:text-[var(--text-ter)]',
  amber:    'bg-[color-mix(in_srgb,var(--p-gold)_12%,transparent)] text-[var(--p-gold)]',
  red:      'bg-[color-mix(in_srgb,var(--p-clay)_12%,transparent)] text-[var(--p-clay)]',
  blue:     'bg-[color-mix(in_srgb,var(--p-label)_12%,transparent)] text-[var(--p-label)]',
};

/** Small inline badge. Uses JetBrains Mono. Square. */
export function Badge({ children, variant = 'default', className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center font-mono text-[10px] uppercase tracking-wider px-2 py-0.5',
        badgeVariants[variant],
        className
      )}
    >
      {children}
    </span>
  );
}

// ── Stat block ────────────────────────────────────────────────────

interface StatProps {
  label: string;
  value: string | number;
  className?: string;
}

/** Label + value. Numbers speak in the instrument voice: mono, tabular. */
export function Stat({ label, value, className }: StatProps) {
  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      <span className="font-body text-[10px] uppercase tracking-[0.22em] text-[var(--p-faint)]">{label}</span>
      <span className="font-mono text-xl text-[var(--p-ink)] [font-variant-numeric:tabular-nums]">{value}</span>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────

interface EmptyStateProps {
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}

/** Empty states mean it: one serif line honoring the emptiness, one quiet action. */
export function EmptyState({ title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('py-12 px-6 text-center', className)}>
      <p className="font-serif text-[17px] text-[var(--p-ink)] mb-1">{title}</p>
      {description && (
        <p className="text-sm text-[var(--p-ink-soft)] mb-4">{description}</p>
      )}
      {action}
    </div>
  );
}

// ── Divider ───────────────────────────────────────────────────────

export function Divider({ className }: { className?: string }) {
  return <hr className={cn('border-[var(--p-hairline)] my-6', className)} />;
}

// ── Callout ───────────────────────────────────────────────────────

interface CalloutProps {
  children: React.ReactNode;
  className?: string;
}

/** The deck's cream callout: 3px gold left rule, serif italic content.
    For the one thing on the page worth saying in the brand's own voice. */
export function Callout({ children, className }: CalloutProps) {
  return (
    <div className={cn('border-l-[3px] border-[var(--p-gold)] bg-[var(--p-cream)] px-7 py-5', className)}>
      <div className="font-serif text-[16px] font-light italic leading-relaxed text-[var(--p-ink)]">{children}</div>
    </div>
  );
}
